import { EmissionSourceSubCategory } from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { NextTFunction } from '@/i18n/client';
import { Document } from '@/typings/common';

import {
  getActivityDataTypeHeader,
  getAttachmentHeader,
  getConvertedUnitDefaultHeader,
  getConvertedUnitHeader,
  getConvertedUsageAmountDefaultHeader,
  getConvertedUsageAmountHeader,
  getDataConfidenceTypeHeader,
  getDataEntrySerialNumberHeader,
  getDateHeader,
  getDatePeriodHeader,
  getDocumentDateDefaultHeader,
  getDocumentDateHeader,
  getDocumentDatePeriodHeader,
  getDocumentNumberHeader,
  getEmissionsHeader,
  getEmissionsUnitHeader,
  getEmissionsWithUnitHeader,
  getFactorErrorTypeHeader,
  getMaskedConvertedUnitHeader,
  getMaskedConvertedUsageAmountHeader,
  getNameHeader,
  getSerialNumberHeader,
  getStatusHeader,
  getSubmittedDeptHeader,
  getSubmittedNameHeader,
  getUnitHeader,
  getUsageAmountHeader,
  getUsageRatioHeader,
  getVisitCountHeader,
  getVisitorHeader,
} from './column-headers/common';
import {
  getCustomerCodeAndNameHeader,
  getProductMaterialsPartNoHeader,
  getReceivingLocationCodeAndNameHeader,
} from './column-headers/downstream-transportation';
import {
  getBusinessTripTypeHeader,
  getCommutingTotalDistanceHeader,
  getEmployeeDivisionDepartmentHeader,
  getEmployeeIdentifierHeader,
} from './column-headers/employee';
import { getShareholdingRatioHeader } from './column-headers/stock';
import {
  getPartNoHeader,
  getShipmentLocationCodeHeader,
  getVendorCodeHeader,
} from './column-headers/upstream-transportation';
import {
  getWasteTransportationUnitHeader,
  getWasteTransportationWeightHeader,
  getWasteTreatmentAmountHeader,
  getWasteTreatmentUnitHeader,
} from './column-headers/waste-generated-in-operations';
import { HeaderTitle, OrgCFVRoute } from './typings';

export const getAllSubcategoriesHeader = (
  t: NextTFunction,
  onViewAttachmentClick: (file: Document) => void,
): HeaderTitle => {
  return {
    [OrgCFVRoute.ENTRY]: {
      [EmissionSourceSubCategory.CAT_1_1]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_1_2]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_1_3]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_1_4]: [
        getDocumentDateHeader(t, {}),
        getMaskedConvertedUsageAmountHeader(t),
        getMaskedConvertedUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_2_1]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getUsageRatioHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_2_2]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getUsageRatioHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_1]: [
        getDataEntrySerialNumberHeader(t, true),
        getDocumentDateHeader(t, { showIconWhenIsBulk: false }),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_2]: [
        getDataEntrySerialNumberHeader(t, true),
        getDocumentDateHeader(t, { showIconWhenIsBulk: false }),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_3]: [
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_4]: [
        getDateHeader(t, {}),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedNameHeader(t),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_5]: [
        getDocumentNumberHeader(t, {}),
        getDocumentDateHeader(t, {}),
        getBusinessTripTypeHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedNameHeader(t),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_1A]: [
        getDataEntrySerialNumberHeader(t, true),
        getDocumentDateHeader(t, { showIconWhenIsBulk: false }),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_1B]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getUsageRatioHeader(t),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_2]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_3]: [
        getDocumentDateHeader(t, {}),
        getWasteTransportationWeightHeader(t),
        getWasteTransportationUnitHeader(t),
        getWasteTreatmentAmountHeader(t),
        getWasteTreatmentUnitHeader(t),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_4]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_5]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_1]: [
        getDataEntrySerialNumberHeader(t, true),
        getDocumentDateHeader(t, { showIconWhenIsBulk: false }),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_2]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_3]: [
        getDataEntrySerialNumberHeader(t, true),
        getDocumentDateHeader(t, { showIconWhenIsBulk: false }),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_4]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_5]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_6]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
    },
    [OrgCFVRoute.APPROVAL]: {
      [EmissionSourceSubCategory.CAT_1_1]: [
        getDocumentDateHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_1_2]: [
        getDocumentDateHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_1_3]: [
        getDocumentDateHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_1_4]: [
        getDocumentDateHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getMaskedConvertedUsageAmountHeader(t),
        getMaskedConvertedUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_2_1]: [
        getDocumentDateHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getUsageRatioHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_2_2]: [
        getDocumentDateHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getUsageRatioHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_3_1]: [
        getSerialNumberHeader(t),
        getDocumentDateHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_2]: [
        getSerialNumberHeader(t),
        getDocumentDateHeader(t, {}),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_3]: [
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_4]: [
        getDocumentDateHeader(t, { label: t('org-cfv-data-entry.date') }),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedNameHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_5]: [
        getDocumentNumberHeader(t, {}),
        getDocumentDateHeader(t, {}),
        getBusinessTripTypeHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedNameHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_1A]: [
        getSerialNumberHeader(t),
        getDocumentDateHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_1B]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getUsageRatioHeader(t),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_2]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_4_3]: [
        getDocumentDateHeader(t, {}),
        getWasteTransportationWeightHeader(t),
        getWasteTransportationUnitHeader(t),
        getWasteTreatmentAmountHeader(t),
        getWasteTreatmentUnitHeader(t),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_4]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_4_5]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_1]: [
        getSerialNumberHeader(t),
        getDocumentDateHeader(t, {}),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_5_2]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_5_3]: [
        getSerialNumberHeader(t),
        getDocumentDateHeader(t, {}),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_5_4]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_5_5]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_5_6]: [
        getSubmittedDeptHeader(t, true),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
    },
    [OrgCFVRoute.COLLECTION]: {
      [EmissionSourceSubCategory.CAT_1_1]: [
        getDocumentDateDefaultHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountDefaultHeader(t),
        getConvertedUnitDefaultHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_1_2]: [
        getDocumentDateDefaultHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountDefaultHeader(t),
        getConvertedUnitDefaultHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_1_3]: [
        getDocumentDateDefaultHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountDefaultHeader(t),
        getConvertedUnitDefaultHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_1_4]: [
        getDocumentDateDefaultHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getMaskedConvertedUsageAmountHeader(t),
        getMaskedConvertedUnitHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_2_1]: [
        getDocumentDateDefaultHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountDefaultHeader(t),
        getConvertedUnitDefaultHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getUsageRatioHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_2_2]: [
        getDocumentDateDefaultHeader(t, {}),
        getSubmittedNameHeader(t),
        getSubmittedDeptHeader(t),
        getConvertedUsageAmountDefaultHeader(t),
        getConvertedUnitDefaultHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getUsageRatioHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_3_1]: [
        getSerialNumberHeader(t, true),
        getDocumentDateDefaultHeader(t, {}),
        getPartNoHeader(t),
        getVendorCodeHeader(t),
        getShipmentLocationCodeHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_3_2]: [
        getSerialNumberHeader(t, true),
        getDocumentDateDefaultHeader(t, {}),
        getProductMaterialsPartNoHeader(t),
        getCustomerCodeAndNameHeader(t),
        getReceivingLocationCodeAndNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_3_3]: [
        getEmployeeIdentifierHeader(t),
        getEmployeeDivisionDepartmentHeader(t),
        getCommutingTotalDistanceHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_4]: [
        getDatePeriodHeader(t),
        getVisitorHeader(t),
        getVisitCountHeader(t),
        getUsageAmountHeader(t),
        getUnitHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedNameHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_3_5]: [
        getDocumentNumberHeader(t, { showIconWhenIsBulk: false }),
        getDocumentDatePeriodHeader(t),
        getBusinessTripTypeHeader(t),
        getUsageAmountHeader(t),
        getUnitHeader(t),
        getEmissionsWithUnitHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedNameHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_1A]: [
        getSerialNumberHeader(t, true),
        getDocumentDateDefaultHeader(t, {}),
        getPartNoHeader(t),
        getVendorCodeHeader(t),
        getShipmentLocationCodeHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_4_1B]: [
        getDocumentDateDefaultHeader(t, {}),
        getConvertedUsageAmountDefaultHeader(t),
        getConvertedUnitDefaultHeader(t),
        getUsageRatioHeader(t),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_2]: [
        getNameHeader(t),
        getFactorErrorTypeHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getDataConfidenceTypeHeader(t),
        getActivityDataTypeHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_3]: [
        getDocumentDateDefaultHeader(t, {}),
        getWasteTransportationWeightHeader(t),
        getWasteTransportationUnitHeader(t),
        getWasteTreatmentAmountHeader(t),
        getWasteTreatmentUnitHeader(t),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getStatusHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_4]: [
        getNameHeader(t),
        getFactorErrorTypeHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getDataConfidenceTypeHeader(t),
        getActivityDataTypeHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_4_5]: [
        getDocumentDateHeader(t, {}),
        getConvertedUsageAmountHeader(t),
        getConvertedUnitHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
        getSubmittedDeptHeader(t),
        getSubmittedNameHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_1]: [
        getSerialNumberHeader(t, false),
        getDocumentDateDefaultHeader(t, {}),
        getProductMaterialsPartNoHeader(t),
        getCustomerCodeAndNameHeader(t),
        getReceivingLocationCodeAndNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_5_2]: [
        getNameHeader(t),
        getFactorErrorTypeHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getActivityDataTypeHeader(t),
        getDataConfidenceTypeHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_3]: [
        getSerialNumberHeader(t, false),
        getDocumentDateDefaultHeader(t, {}),
        getProductMaterialsPartNoHeader(t),
        getCustomerCodeAndNameHeader(t),
        getReceivingLocationCodeAndNameHeader(t),
        getAttachmentHeader(t, onViewAttachmentClick),
      ],
      [EmissionSourceSubCategory.CAT_5_4]: [
        getNameHeader(t),
        getFactorErrorTypeHeader(t),
        getShareholdingRatioHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getActivityDataTypeHeader(t),
        getDataConfidenceTypeHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_5]: [
        getNameHeader(t),
        getFactorErrorTypeHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getActivityDataTypeHeader(t),
        getDataConfidenceTypeHeader(t),
      ],
      [EmissionSourceSubCategory.CAT_5_6]: [
        getNameHeader(t),
        getFactorErrorTypeHeader(t),
        getEmissionsHeader(t),
        getEmissionsUnitHeader(t),
        getActivityDataTypeHeader(t),
        getDataConfidenceTypeHeader(t),
      ],
    },
  };
};
